import React from 'react';
import styled, { css } from 'styled-components/macro';
import { MOBILE_LANDSCAPE, TABLET_LANDSCAPE, TABLET_PORTRAIT } from 'src/client/constants/mediaQueries';
import type { UpgradeProductListing } from './types';

type Props = {
  getPlanUrl: (product: UpgradeProductListing) => string;
  onHide: () => void;
  products: UpgradeProductListing[];
};

const formatInitialPeriod = (product: UpgradeProductListing) => {
  if (product.recurringPeriodText) {
    return product.recurringPeriodText;
  }

  if (!product.initialPeriod) {
    return 'Membership';
  }

  if (product.initialPeriod === 1) {
    return '1 Day';
  }

  return `${product.initialPeriod} Days`;
};

const formatRebillText = (product: UpgradeProductListing) => product.recurringText || product.perPeriod || '';

export const PlanCards = (props: Props) => {
  const { getPlanUrl, onHide, products } = props;

  return (
    <PlanList>
      {products.map((product) => (
        <PlanCard key={product.productUUID} highlighted={Boolean(product.productIsDiscounted)}>
          <InitialTerms>{formatInitialPeriod(product)}</InitialTerms>
          <Divider highlighted={Boolean(product.productIsDiscounted)} />
          <Price>
            <Amount>
              <Currency>{product.currencySymbol}</Currency>
              {product.initialPrice.split('.')[0]}
              <Cents>{product.initialPrice.split('.')[1] || '00'}</Cents>
            </Amount>
          </Price>
          <PlanButton href={getPlanUrl(product)} onClick={onHide} highlighted={Boolean(product.productIsDiscounted)}>
            Select Plan
          </PlanButton>
          <RebillTerms>{formatRebillText(product)}</RebillTerms>
        </PlanCard>
      ))}
    </PlanList>
  );
};

const PlanList = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media ${TABLET_PORTRAIT} {
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    gap: 16px;
  }

  @media ${TABLET_LANDSCAPE} {
    flex-wrap: nowrap;
  }
`;

const PlanCard = styled.div<{ highlighted: boolean }>`
  background-color: #e1e2e7;
  border-radius: 4px;
  color: #000000;
  overflow: hidden;
  padding: 16px 20px;
  position: relative;
  transition: transform 0.2s ease-in-out;
  width: 100%;

  &:hover {
    transform: scale(1.03);
  }

  @media ${MOBILE_LANDSCAPE} {
    margin: 0 auto;
    max-width: 340px;
  }

  @media ${TABLET_PORTRAIT} {
    margin: 0;
    max-width: 300px;
    padding: 16px 24px;
  }

  ${({ highlighted }) =>
    highlighted
      ? css`
          background-color: #ff504a;
          color: #ffffff;
        `
      : ''}
`;

const InitialTerms = styled.p`
  font-size: 16px;
  margin: 8px 0 10px;
  text-align: center;
`;

const Divider = styled.hr<{ highlighted: boolean }>`
  border-color: ${({ highlighted }) => (highlighted ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)')};
  border-top-width: 1px;
  margin: 0;
`;

const Price = styled.div`
  font-weight: 600;
  margin: 0;
  text-align: center;
`;

const Amount = styled.span`
  font-size: 46px;
  letter-spacing: -2px;

  @media ${MOBILE_LANDSCAPE} {
    font-size: 52px;
  }
`;

const Currency = styled.span`
  font-size: 12px;
  left: -4px;
  position: relative;
  top: -22px;
`;

const Cents = styled.span`
  font-size: 16px;
  letter-spacing: normal;
  position: relative;
  right: -2px;
  top: -24px;
`;

const RebillTerms = styled.span`
  display: block;
  font-size: 12px;
  margin: 8px 0 14px;
  min-height: 32px;
  text-align: center;

  @media ${TABLET_PORTRAIT} {
    font-size: 10px;
  }
`;

const PlanButton = styled.a<{ highlighted: boolean }>`
  align-items: center;
  background-color: ${({ highlighted }) => (highlighted ? '#000000' : '#5d616a')};
  border-radius: 4px;
  color: #fff;
  display: inline-flex;
  font-size: 16px;
  font-weight: 700;
  height: 46px;
  justify-content: center;
  text-decoration: none;
  width: 100%;

  @media ${TABLET_PORTRAIT} {
    height: 42px;
  }

  &:hover,
  &:focus {
    color: #fff;
    opacity: 0.92;
    text-decoration: none;
  }
`;
